import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { FileUpload } from '@/components/shared/components/forms/FileUpload';

interface AddInfoRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    fullName: string;
    description: string;
    photoUrl?: string;
    resumeUrl?: string;
    isResumePublic: boolean;
    forSelf: boolean;
  }) => void;
}

export function AddInfoRequestDialog({
  open,
  onClose,
  onSubmit,
}: AddInfoRequestDialogProps) {
  const [formData, setFormData] = useState({
    fullName: '',
    description: '',
    photoUrl: '',
    resumeUrl: '',
    isResumePublic: false,
    forSelf: true,
  });

  const handleSubmit = () => {
    if (!formData.fullName.trim() || !formData.description.trim()) return;
    onSubmit(formData);
    setFormData({
      fullName: '',
      description: '',
      photoUrl: '',
      resumeUrl: '',
      isResumePublic: false,
      forSelf: true,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-[100vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>طلب إضافة معلومات شخصية</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-6">
            <div className="space-y-4">
              <Label>نوع الطلب</Label>
              <RadioGroup
                value={formData.forSelf ? 'self' : 'other'}
                onValueChange={(value) => 
                  setFormData(prev => ({ ...prev, forSelf: value === 'self' }))
                }
                className="flex flex-col gap-2"
              >
                <div className="flex items-center justify-end gap-2">
                  <Label htmlFor="self" className="cursor-pointer">
                    أقوم بتعبئة معلوماتي الشخصية
                  </Label>
                  <RadioGroupItem value="self" id="self" />
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Label htmlFor="other" className="cursor-pointer">
                    أقوم بتعبئة معلومات شخص آخر
                  </Label>
                  <RadioGroupItem value="other" id="other" />
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="fullName">الاسم الرباعي (بدون اسم العائلة)</Label>
              <Input
                id="fullName"
                value={formData.fullName}
                onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                placeholder="أدخل الاسم الرباعي"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">نبذة مختصرة</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="اكتب نبذة مختصرة"
              />
            </div>

            <div className="space-y-2">
              <Label>الصورة الشخصية (اختياري)</Label>
              <FileUpload
                type="image"
                value={formData.photoUrl}
                onChange={(value) => setFormData(prev => ({ ...prev, photoUrl: value }))}
                label="الصورة الشخصية"
              />
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>السيرة الذاتية PDF (اختياري)</Label>
                <FileUpload
                  type="pdf"
                  value={formData.resumeUrl}
                  onChange={(value) => setFormData(prev => ({ ...prev, resumeUrl: value }))}
                  label="ملف السيرة الذاتية"
                />
              </div>

              {formData.resumeUrl && (
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <Switch
                    checked={formData.isResumePublic}
                    onCheckedChange={(checked) => 
                      setFormData(prev => ({ ...prev, isResumePublic: checked }))
                    }
                  />
                  <Label className="text-sm text-gray-700">
                    جعل السيرة الذاتية متاحة للجميع
                  </Label>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 sm:justify-start border-t pt-4">
          <Button
            onClick={handleSubmit}
            disabled={!formData.fullName.trim() || !formData.description.trim()}
            className="hover:bg-secondary"
          >
            إرسال الطلب
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            className="mr-2"
          >
            إلغاء
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}